import { useEffect, useState, type KeyboardEvent } from "react";
import "./multimenu.css";

type ProductLink = {
  label: string;
  image: string;
};

const productLinks: ProductLink[] = [
  {
    label: "Olaf",
    image: "https://cdn.prod.website-files.com/686e38e4cff3784e230d2c25/686e567e67f6622d88a2e1c6_nav-img-1.avif",
  },
  {
    label: "Stijn",
    image: "https://cdn.prod.website-files.com/686e38e4cff3784e230d2c25/686e567fc4fcdd19e150e190_nav-img-2.avif",
  },
  {
    label: "Kees",
    image: "https://cdn.prod.website-files.com/686e38e4cff3784e230d2c25/686e567e2fca1779de2a029c_nav-img-3.avif",
  },
  {
    label: "Nelis",
    image: "https://cdn.prod.website-files.com/686e38e4cff3784e230d2c25/686e567fc4fcdd19e150e18b_nav-img-4.avif",
  },
];

const serviceLinks = ["Custom Pieces", "Renovation", "Wholesale"];

function ArrowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 16 16" fill="none" className="mm-icon">
      <path d="M9.33398 12.6666L14.0007 7.99992L9.33398 3.33325" stroke="currentColor" strokeMiterlimit="10" />
      <path d="M14.0007 8H1.33398" stroke="currentColor" strokeMiterlimit="10" />
    </svg>
  );
}

export function MultimenuPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    document.title = "Multimenu | Yash Bindal";
    const descriptionTag =
      document.querySelector('meta[name="description"]') ?? document.createElement("meta");

    descriptionTag.setAttribute("name", "description");
    descriptionTag.setAttribute("content", "A responsive multimenu interaction in React");
    if (!descriptionTag.parentElement) {
      document.head.appendChild(descriptionTag);
    }
  }, []);

  useEffect(() => {
    let timer: number | null = null;

    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const debouncedResize = () => {
      if (timer) {
        window.clearTimeout(timer);
      }
      timer = window.setTimeout(updateIsMobile, 200);
    };

    updateIsMobile();
    window.addEventListener("resize", debouncedResize);

    return () => {
      window.removeEventListener("resize", debouncedResize);
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const isDropdownElement = target.closest("[data-mm-dropdown-toggle], [data-mm-dropdown]");
      if (!isDropdownElement) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("click", handler);
    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(current => {
      const next = !current;
      if (!next) {
        setOpenDropdown(null);
      }
      return next;
    });
  };

  const toggleDropdown = (index: number) => {
    setOpenDropdown(current => (current === index ? null : index));
  };

  const handleDropdownKeydown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleDropdown(index);
      return;
    }

    if (event.key === "Escape") {
      setOpenDropdown(null);
      event.currentTarget.focus();
      return;
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }

    event.preventDefault();
    const links = Array.from(
      document.querySelectorAll<HTMLAnchorElement>(`[data-mm-dropdown="${index}"] .mm-nav-dropdown__link`)
    );
    if (links.length === 0) {
      return;
    }

    const currentIndex = links.indexOf(document.activeElement as HTMLAnchorElement);

    if (event.key === "ArrowDown") {
      links[(currentIndex + 1) % links.length]?.focus();
      return;
    }

    links[(currentIndex - 1 + links.length) % links.length]?.focus();
  };

  const navHasDropdownOpen = openDropdown !== null;

  return (
    <div className="mm-hero-section">
      <div
        className="mm-hero-background"
        style={{
          backgroundImage:
            'url("https://cdn.prod.website-files.com/686e38e4cff3784e230d2c25/686e567f9b8e8cf7b247a237_header-img.avif")',
        }}
      />

      <nav className={`mm-nav ${menuOpen ? "is-menu-open" : ""} ${navHasDropdownOpen ? "has-dropdown-open" : ""}`}>
        <div className="mm-nav-container">
          <div className="mm-nav-bg" />

          <div className="mm-nav-inner">
            <a href="/" className="mm-nav-logo" aria-label="Home">
              OSMO
            </a>

            <div
              id="mobile-navigation"
              role="navigation"
              aria-label="Main navigation"
              className={`mm-nav-center ${menuOpen ? "is-open" : ""}`}
            >
              <ul className="mm-nav-center__list">
                <li
                  onMouseEnter={() => {
                    if (!isMobile) {
                      setOpenDropdown(0);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isMobile) {
                      setOpenDropdown(null);
                    }
                  }}
                >
                  <button
                    data-mm-dropdown-toggle=""
                    data-mm-dropdown="0"
                    aria-expanded={openDropdown === 0}
                    aria-haspopup="true"
                    aria-controls="dropdown-0"
                    onClick={() => toggleDropdown(0)}
                    onKeyDown={event => handleDropdownKeydown(event, 0)}
                    className={`mm-nav-link ${openDropdown === 0 ? "is-open" : ""}`}
                    type="button"
                  >
                    <span className="mm-nav-link__label">Products</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      viewBox="0 0 17 10"
                      fill="none"
                      className="mm-nav-link__dropdown-icon"
                    >
                      <path
                        d="M1.5 1.5L8.5 8.5L15.5 1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <div
                    id="dropdown-0"
                    data-mm-dropdown="0"
                    role="menu"
                    aria-hidden={openDropdown !== 0}
                    className={`mm-nav-dropdown ${openDropdown === 0 ? "is-open" : ""}`}
                  >
                    <div className="mm-nav-dropdown__overflow">
                      <div className="mm-nav-dropdown__overflow-inner">
                        <div className="mm-nav-container">
                          <ul className="mm-nav-dropdown__content">
                            {productLinks.map(link => (
                              <li className="mm-nav-dropdown__content-li" key={link.label}>
                                <a href="/" role="menuitem" className="mm-nav-dropdown__link">
                                  <div className="mm-nav-dropdown__link-bg">
                                    <img src={link.image} className="mm-nav-dropdown__img" alt={link.label} />
                                    <div className="mm-nav-dropdown__img-overlay" />
                                  </div>
                                  <div className="mm-nav-dropdown__link-inner">
                                    <span className="mm-nav-dropdown__link-label">{link.label}</span>
                                    <div className="mm-nav-dropdown__link-bubble">
                                      <ArrowIcon />
                                    </div>
                                  </div>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                <li
                  onMouseEnter={() => {
                    if (!isMobile) {
                      setOpenDropdown(1);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isMobile) {
                      setOpenDropdown(null);
                    }
                  }}
                >
                  <button
                    data-mm-dropdown-toggle=""
                    data-mm-dropdown="1"
                    aria-expanded={openDropdown === 1}
                    aria-haspopup="true"
                    aria-controls="dropdown-1"
                    onClick={() => toggleDropdown(1)}
                    onKeyDown={event => handleDropdownKeydown(event, 1)}
                    className={`mm-nav-link ${openDropdown === 1 ? "is-open" : ""}`}
                    type="button"
                  >
                    <span className="mm-nav-link__label">Services</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      viewBox="0 0 17 10"
                      fill="none"
                      className="mm-nav-link__dropdown-icon"
                    >
                      <path
                        d="M1.5 1.5L8.5 8.5L15.5 1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <div
                    id="dropdown-1"
                    data-mm-dropdown="1"
                    role="menu"
                    aria-hidden={openDropdown !== 1}
                    className={`mm-nav-dropdown ${openDropdown === 1 ? "is-open" : ""}`}
                  >
                    <div className="mm-nav-dropdown__overflow">
                      <div className="mm-nav-dropdown__overflow-inner">
                        <div className="mm-nav-container">
                          <ul className="mm-nav-dropdown__content">
                            {serviceLinks.map(service => (
                              <li className="mm-nav-dropdown__content-li" key={service}>
                                <a href="/" role="menuitem" className="mm-nav-dropdown__link is--static">
                                  <div className="mm-nav-dropdown__link-inner">
                                    <span className="mm-nav-dropdown__link-label">{service}</span>
                                    <div className="mm-nav-dropdown__link-bubble">
                                      <ArrowIcon />
                                    </div>
                                  </div>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                <li>
                  <a href="/about" className="mm-nav-link">
                    <span className="mm-nav-link__label">About</span>
                  </a>
                </li>
                <li>
                  <a href="/news" className="mm-nav-link">
                    <span className="mm-nav-link__label">News</span>
                  </a>
                </li>
              </ul>
            </div>

            <div className="mm-nav-end">
              <a href="/contact" className="mm-nav-button md--hide">
                <span>Contact Us</span>
              </a>
              <a href="/enquire" className="mm-nav-button is--primary">
                <span>Enquire Now</span>
              </a>
              <button
                aria-label="toggle menu"
                aria-expanded={menuOpen}
                aria-controls="mobile-navigation"
                onClick={toggleMenu}
                className="mm-menu-button"
                type="button"
              >
                <div className="mm-menu-button__line" />
                <div className="mm-menu-button__line" />
              </button>
            </div>
          </div>
        </div>
      </nav>
      <div className="mm-page-bg" />
    </div>
  );
}
